import _ from "lodash";
import {
  ALL_STRINGS,
  NOTE_NAMES,
  NotePosition,
  ToneSpec,
  chooseRootSpelling,
  findAllInRange,
  formatNoteName,
  tonesToLabels,
  tonesToNotes,
} from "./notes";

/**
 * Everything the board can highlight for a chosen root: the root alone (CAGED-style), a scale,
 * or a chord's tones. Each is a `ToneSpec[]` so its spelling, degrees and labels stay in sync.
 */
export interface OverlayDefinition {
  /** Lowercase so it reads after a key name: "A minor pentatonic", "G dominant 7 chord". */
  label: string;
  group: "Roots" | "Scales" | "Chords";
  tones: ToneSpec[];
}

const tone = (semitone: number, degree: number, label: string): ToneSpec => ({
  semitone,
  degree,
  label,
});

export const OVERLAYS = {
  roots: { label: "root notes", group: "Roots", tones: [tone(0, 1, "1")] },
  minorPentatonic: {
    label: "minor pentatonic",
    group: "Scales",
    tones: [tone(0, 1, "1"), tone(3, 3, "b3"), tone(5, 4, "4"), tone(7, 5, "5"), tone(10, 7, "b7")],
  },
  majorPentatonic: {
    label: "major pentatonic",
    group: "Scales",
    tones: [tone(0, 1, "1"), tone(2, 2, "2"), tone(4, 3, "3"), tone(7, 5, "5"), tone(9, 6, "6")],
  },
  majorScale: {
    label: "major scale",
    group: "Scales",
    tones: [
      tone(0, 1, "1"),
      tone(2, 2, "2"),
      tone(4, 3, "3"),
      tone(5, 4, "4"),
      tone(7, 5, "5"),
      tone(9, 6, "6"),
      tone(11, 7, "7"),
    ],
  },
  naturalMinorScale: {
    label: "natural minor",
    group: "Scales",
    tones: [
      tone(0, 1, "1"),
      tone(2, 2, "2"),
      tone(3, 3, "b3"),
      tone(5, 4, "4"),
      tone(7, 5, "5"),
      tone(8, 6, "b6"),
      tone(10, 7, "b7"),
    ],
  },
  majorChord: {
    label: "major chord",
    group: "Chords",
    tones: [tone(0, 1, "1"), tone(4, 3, "3"), tone(7, 5, "5")],
  },
  minorChord: {
    label: "minor chord",
    group: "Chords",
    tones: [tone(0, 1, "1"), tone(3, 3, "b3"), tone(7, 5, "5")],
  },
  dominantSeventhChord: {
    label: "dominant 7 chord",
    group: "Chords",
    tones: [tone(0, 1, "1"), tone(4, 3, "3"), tone(7, 5, "5"), tone(10, 7, "b7")],
  },
  majorSeventhChord: {
    label: "major 7 chord",
    group: "Chords",
    tones: [tone(0, 1, "1"), tone(4, 3, "3"), tone(7, 5, "5"), tone(11, 7, "7")],
  },
  minorSeventhChord: {
    label: "minor 7 chord",
    group: "Chords",
    tones: [tone(0, 1, "1"), tone(3, 3, "b3"), tone(7, 5, "5"), tone(10, 7, "b7")],
  },
} satisfies Record<string, OverlayDefinition>;

export type OverlayKind = "none" | keyof typeof OVERLAYS;

export const OVERLAY_KINDS: OverlayKind[] = [
  "none",
  ...(Object.keys(OVERLAYS) as (keyof typeof OVERLAYS)[]),
];

export const OVERLAY_DEGREE_LABELS: Record<
  keyof typeof OVERLAYS,
  Record<number, string>
> = _.mapValues(OVERLAYS, (overlay) => tonesToLabels(overlay.tones));

/**
 * How the root of this overlay is named for a pitch class. A scale or chord picks the single
 * spelling that suits it ("Eb" major pentatonic but "D#" minor); the root-only overlay, and the
 * key picker while no overlay is on, have no scale to spell against and keep the dual name.
 */
export function overlayRootSpelling(pitchClass: number, kind: OverlayKind): string {
  if (kind === "none" || kind === "roots") return NOTE_NAMES[pitchClass];
  return chooseRootSpelling(pitchClass, OVERLAYS[kind].tones);
}

/** The overlay's notes, spelled from its root, each paired with its degree — empty for "none". */
export function overlayNotes(
  pitchClass: number,
  kind: OverlayKind
): { noteName: string; degree: number }[] {
  if (kind === "none") return [];
  if (kind === "roots") return [{ noteName: NOTE_NAMES[pitchClass], degree: 1 }];
  return tonesToNotes(overlayRootSpelling(pitchClass, kind), OVERLAYS[kind].tones);
}

export function findOverlayPositions(
  pitchClass: number,
  kind: OverlayKind,
  startFret: number,
  endFret: number
): NotePosition[] {
  return findAllInRange(ALL_STRINGS, startFret, endFret, overlayNotes(pitchClass, kind));
}

/** The overlay as a phrase for the summary row: "A minor pentatonic", "A♯/B♭ root notes". */
export function overlayTitle(pitchClass: number, kind: OverlayKind): string {
  if (kind === "none") return "No overlay";
  return `${formatNoteName(overlayRootSpelling(pitchClass, kind))} ${OVERLAYS[kind].label}`;
}
